/* V20 Business Rule: Trockeneis-Scheiben werden ausschließlich über Laatzen geliefert und können nur dort abgeholt werden. */
(() => {
  'use strict';
  if (window.__tpv20ScheibenPickupGuard) return;
  window.__tpv20ScheibenPickupGuard = true;

  const ALLOWED_LOCATION = 'hannover';
  const NOTE = 'Trockeneis-Scheiben: Lieferung ausschließlich über Laatzen, Abholung ausschließlich in Laatzen.';
  const DIRECT_SCHEIBEN_USECASES = new Set(['food', 'transport', 'emergency']);
  const observedRoots = new WeakSet();
  const syncingRoots = new WeakSet();
  const queuedRoots = new WeakSet();

  const control = (root, key) => key === 'usecase'
    ? root.querySelector('[data-tpv20-usecase]')
    : root.querySelector(`[data-tpv20-field="${key}"]`);

  const rememberContext = root => {
    const usecase = control(root, 'usecase');
    if (usecase?.value) root.dataset.tpv20ScheibenGuardUsecase = String(usecase.value);
    const coolingType = control(root, 'coolingType');
    if (coolingType?.value) root.dataset.tpv20ScheibenGuardCoolingType = String(coolingType.value);
  };

  const currentProductKey = root => {
    rememberContext(root);
    const usecase = String(
      control(root, 'usecase')?.value ||
      root.dataset.tpv20ScheibenGuardUsecase ||
      root.dataset.tpv20DefaultUsecase ||
      ''
    ).trim();
    if (usecase === 'lab') {
      const coolingType = String(
        control(root, 'coolingType')?.value ||
        root.dataset.tpv20ScheibenGuardCoolingType ||
        'hold'
      );
      return coolingType === 'rapid' ? 'pellets' : 'scheiben';
    }
    return DIRECT_SCHEIBEN_USECASES.has(usecase) ? 'scheiben' : '';
  };

  const pickupSelected = root => String(control(root, 'deliveryType')?.value || '') === 'pickup';

  const pickupField = select => select?.closest('[data-tpv20-field-wrap="pickupLocation"]') || select?.closest('.tpv20-field');

  const ensureNote = field => {
    if (!field) return null;
    let note = field.querySelector('[data-tpv20-scheiben-pickup-note]');
    if (!note) {
      note = document.createElement('small');
      note.className = 'tpv20-field-guide';
      note.dataset.tpv20ScheibenPickupNote = '';
      note.dataset.state = 'success';
      note.setAttribute('role', 'status');
      note.setAttribute('aria-live', 'polite');
      field.appendChild(note);
    }
    return note;
  };

  const syncRoot = root => {
    if (!(root instanceof Element) || syncingRoots.has(root)) return;
    syncingRoots.add(root);
    try {
      rememberContext(root);
      const select = control(root, 'pickupLocation');
      if (!(select instanceof HTMLSelectElement)) return;

      const scheiben = currentProductKey(root) === 'scheiben';
      const field = pickupField(select);
      const note = ensureNote(field);

      Array.from(select.options).forEach(option => {
        if (!option.value || option.value === ALLOWED_LOCATION) return;
        option.disabled = scheiben;
        option.hidden = scheiben;
      });

      if (note) {
        note.hidden = !scheiben;
        note.textContent = scheiben ? NOTE : '';
        note.dataset.state = 'success';
      }

      root.dataset.tpv20ScheibenPickupGuard = scheiben ? 'active' : 'inactive';

      if (scheiben && pickupSelected(root) && select.value !== ALLOWED_LOCATION) {
        select.value = ALLOWED_LOCATION;
        select.removeAttribute('aria-invalid');
        field?.classList.remove('is-error');
        select.dispatchEvent(new Event('input', { bubbles: true }));
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    } finally {
      syncingRoots.delete(root);
    }
  };

  const invalidPickup = root => {
    if (currentProductKey(root) !== 'scheiben' || !pickupSelected(root)) return false;
    return String(control(root, 'pickupLocation')?.value || '') !== ALLOWED_LOCATION;
  };

  const blockInvalidAction = (event, root) => {
    if (!invalidPickup(root)) return false;
    event.preventDefault();
    event.stopImmediatePropagation();

    const select = control(root, 'pickupLocation');
    const field = pickupField(select);
    field?.classList.add('is-error');
    select?.setAttribute('aria-invalid', 'true');

    const note = ensureNote(field);
    if (note) {
      note.hidden = false;
      note.dataset.state = 'danger';
      note.textContent = NOTE;
    }

    const status = root.querySelector('[data-tpv20-status]');
    if (status) status.textContent = NOTE;
    field?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    return true;
  };

  const queueSync = root => {
    if (!(root instanceof Element) || queuedRoots.has(root)) return;
    queuedRoots.add(root);
    requestAnimationFrame(() => {
      queuedRoots.delete(root);
      syncRoot(root);
    });
  };

  const bindRoot = root => {
    if (!(root instanceof Element) || observedRoots.has(root)) return;
    observedRoots.add(root);
    rememberContext(root);
    queueSync(root);

    new MutationObserver(() => queueSync(root)).observe(root, {
      subtree: true,
      childList: true
    });

    root.addEventListener('input', event => {
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLSelectElement) {
        rememberContext(root);
        queueSync(root);
      }
    }, true);

    root.addEventListener('change', event => {
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLSelectElement) {
        rememberContext(root);
        queueSync(root);
      }
    }, true);

    root.addEventListener('click', event => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target?.closest('[data-tpv20-next],[data-tpv20-finish],[data-tpv20-cart]')) return;
      blockInvalidAction(event, root);
    }, true);
  };

  const run = (scope = document) => {
    if (scope instanceof Element && scope.matches('[data-tpv20-root]')) bindRoot(scope);
    scope.querySelectorAll?.('[data-tpv20-root]').forEach(bindRoot);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => run(document), { once: true });
  } else {
    run(document);
  }
  document.addEventListener('shopify:section:load', event => run(event.target));
  window.addEventListener('pageshow', () => run(document));
})();
